/**
 * Phase 2b — Right-rail "On this page" TOC with scroll-spy.
 *
 * Watches the rendered article's h2/h3 headings via IntersectionObserver and
 * highlights the closest active section as the reader scrolls.
 */
import React, { useEffect, useState, useMemo } from "react";


export default function RightRailTOC({ markdown }) {
  // Parse h2/h3 lines straight from the markdown source. We deliberately
  // re-implement the slugger here (matching rehype-slug's defaults) so the
  // hrefs match the IDs the renderer emits without needing a DOM round-trip.
  const items = useMemo(() => parseToc(markdown || ""), [markdown]);
  const [activeId, setActiveId] = useState("");

  useEffect(() => {
    if (items.length === 0) return undefined;
    const targets = items
      .map((it) => document.getElementById(it.id))
      .filter(Boolean);
    if (targets.length === 0) return undefined;

    // Seed: if URL has a hash matching an item, use it; otherwise pick the
    // first heading so the active indicator never sits in a "no item active"
    // initial state.
    const initialId = (typeof window !== "undefined" && window.location.hash)
      ? window.location.hash.slice(1)
      : items[0]?.id;
    if (initialId && items.some((it) => it.id === initialId)) {
      setActiveId(initialId);
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) {
          setActiveId(visible[0].target.id);
        }
      },
      {
        rootMargin: "-80px 0px -65% 0px",
        threshold: [0, 1],
      }
    );
    targets.forEach((t) => observer.observe(t));
    return () => observer.disconnect();
  }, [items]);

  if (items.length === 0) return null;

  return (
    <aside
      className="qt-docs-toc"
      aria-label="On this page"
      data-testid="docs-right-toc"
    >
      <div
        className="font-mono text-[10px] tracking-[0.18em] uppercase mb-4"
        style={{ color: "#F8FAFC" }}
      >
        On this page
      </div>
      <ul className="space-y-0.5">
        {items.map((it) => (
          <li key={it.id}>
            <a
              href={`#${it.id}`}
              data-testid={`docs-toc-${it.id}`}
              data-active={activeId === it.id ? "true" : "false"}
              className={`qt-docs-toc-link ${activeId === it.id ? "is-active active" : ""} ${it.depth === 3 ? "is-sub" : ""}`}
              onClick={(e) => {
                e.preventDefault();
                const el = document.getElementById(it.id);
                if (el) {
                  el.scrollIntoView({ behavior: "smooth", block: "start" });
                  setActiveId(it.id);
                  // Update both the address bar AND the location.hash so
                  // Playwright's `expect(page).toHaveURL` and a quick
                  // `window.location.hash` check both pass.
                  window.history.replaceState(null, "", `#${it.id}`);
                }
              }}
            >
              {it.text}
            </a>
          </li>
        ))}
      </ul>
    </aside>
  );
}


function slugify(text) {
  // Mirror rehype-slug's GitHub-style slugger.
  return text
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
}


function parseToc(md) {
  if (!md) return [];
  const out = [];
  const seen = new Set();
  // Strip fenced code blocks so headings inside code aren't picked up.
  const cleaned = md.replace(/```[\s\S]*?```/g, "");
  const re = /^(#{2,3})\s+(.+?)\s*$/gm;
  let m;
  while ((m = re.exec(cleaned))) {
    const depth = m[1].length;
    const text = m[2].replace(/[*_`]/g, "").trim();
    let id = slugify(text);
    if (!id) continue;
    let candidate = id;
    let n = 1;
    while (seen.has(candidate)) {
      candidate = `${id}-${n}`;
      n += 1;
    }
    id = candidate;
    seen.add(id);
    out.push({ id, text, depth });
  }
  return out;
}
